import { PropsWithChildren } from "react";
import { useRouter } from "next/router";
import { GridBox } from "@pagepro-monorepo/ui/lib/components/styles/Grid";
import Typography from "@pagepro-monorepo/ui/lib/components/styles/Typography";

import Image from "@components/atoms/Image";
import TechStack from "@components/molecules/TechStack";
import Link from "@components/atoms/Link";
import { getBreadcrumbs } from "@components/utils/Seo/utils";

import { LandingPageHeroProps } from "./types";
import * as Styled from "./styles";

const LandingPageHero: React.FC<
  PropsWithChildren<LandingPageHeroProps>
> = ({
  title,
  description,
  icons,
  $color,
  backgroundImage,
  firstText,
  secondText,
  breadcrumbs,
  children
}) => {
  const isBottomContent = firstText || secondText;

  const { asPath } = useRouter();

  const [current, ...breadcrumbPaths] =
    getBreadcrumbs(asPath).reverse();

  return (
    <GridBox
      gridTemplateColumns={["1fr", "", "repeat(2, 1fr)"]}
      gridGap={["4rem", "4.75rem", "3rem"]}
      justifyContent="space-between"
      alignItems={isBottomContent ? "end" : "center"}
      p={["3.5rem 0 1.25rem", "8.5rem 0 1.25rem", "5.5rem 0 7.75rem"]}
      aria-label="Hero"
    >
      {backgroundImage && (
        <Styled.Figure>
          <Image
            {...{ ...backgroundImage }}
            priority
            style={{
              objectFit: "cover"
            }}
            fill
          />
        </Styled.Figure>
      )}
      <Styled.Content
        {...{ $color }}
        aria-label="Hero content container"
      >
        <Styled.Breadcrumbs
          title="Breadcrumbs"
          aria-label="Breadcrumbs"
        >
          {breadcrumbs || (
            <ul>
              {breadcrumbPaths.map(({ name, url }) => (
                <li key={url}>
                  <Link href={url}>{name}</Link>
                </li>
              ))}
              <li>
                <span>{current?.name}</span>
              </li>
            </ul>
          )}
        </Styled.Breadcrumbs>

        <Styled.Title>
          {typeof title === "string" ? (
            <Typography variant="heading1">{title}</Typography>
          ) : (
            title
          )}
        </Styled.Title>
        {description && (
          <Styled.Description>
            {typeof description === "string" ? (
              <Typography variant="body8">{description}</Typography>
            ) : (
              description
            )}
          </Styled.Description>
        )}
        {icons && <TechStack items={icons} />}
        {isBottomContent && (
          <GridBox
            gridTemplateColumns={["", "repeat(2, 1fr)", ""]}
            gridGap="2.25rem"
            justifyContent="space-between"
            mt={["3.375rem", "", "8.75rem"]}
          >
            {typeof firstText === "string" ? (
              <Typography variant="body8">{firstText}</Typography>
            ) : (
              firstText
            )}
            {typeof secondText === "string" ? (
              <Typography variant="body8">{secondText}</Typography>
            ) : (
              secondText
            )}
          </GridBox>
        )}
      </Styled.Content>
      <Styled.FormWrapper justifyContent="center">
        <Styled.Form>{children}</Styled.Form>
      </Styled.FormWrapper>
    </GridBox>
  );
};

export default LandingPageHero;
